import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import CustomerNegotiationsTab from '../components/CustomerNegotiationsTab';
import { 
  ShoppingBag, 
  MessageCircle, 
  Package, 
  DollarSign,
  TrendingUp,
  Link
} from 'lucide-react';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  stock: number;
  image_url: string;
  thumbnail_url: string;
}

interface CartItem {
  id: number;
  product_id: number;
  quantity: number;
  product: {
    id: number;
    name: string;
    price: number;
    image_url: string;
    thumbnail_url: string;
  };
}

interface OrderItem {
  id: number;
  product_id: number;
  quantity: number;
  price_per_unit: number;
}

interface Order {
  id: number;
  total_price: number;
  status: string;
  created_at: string;
  items: OrderItem[];
}

interface CustomerStats {
  totalOrders: number;
  totalSpent: number;
  cartItemsCount: number;
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  payment_pending: 'En attente de paiement',
  payment_confirmed: 'Paiement confirmé',
  preparing: 'En préparation',
  ready: 'Prête à expédier',
  paid: 'Payée',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
};

const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  payment_pending: 'bg-yellow-100 text-yellow-800',
  payment_confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-orange-100 text-orange-800',
  ready: 'bg-green-100 text-green-800',
  paid: 'bg-yellow-100 text-yellow-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-500 text-white',
  cancelled: 'bg-red-100 text-red-800',
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState<CustomerStats>({
    totalOrders: 0,
    totalSpent: 0,
    cartItemsCount: 0
  });

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const userResponse = await api.get('/auth/me');
        const userData = userResponse.data;

        if (!userData.is_customer) {
          navigate('/');
          return;
        }

        const [statsRes, productsRes, cartRes, ordersRes] = await Promise.all([
          api.get('/customer/stats'),
          api.get('/customer/products'),
          api.get('/customer/cart'),
          api.get('/customer/orders'),
        ]);

        setStats(statsRes.data);
        setProducts(productsRes.data);
        setCartItems(cartRes.data);
        setOrders(ordersRes.data);

        if (location.state?.orderSuccess) {
          setSuccessMessage(location.state.message || 'Commande passée avec succès !');
          setActiveTab('orders');
          window.history.replaceState({}, document.title);
        }

      } catch (err: any) {
        console.error('Erreur lors du chargement des données:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError('Erreur lors du chargement du tableau de bord. Veuillez réessayer.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [navigate, location.state]);

  const handleAddToCart = async (productId: number, quantity: number = 1) => {
    try {
      await api.post('/customer/cart', {
        product_id: productId,
        quantity: quantity
      });

      const cartResponse = await api.get('/customer/cart');
      setCartItems(cartResponse.data);

      const statsResponse = await api.get('/customer/stats');
      setStats(statsResponse.data);

      setActiveTab('cart');
    } catch (err) {
      console.error('Erreur lors de l\'ajout au panier:', err);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    }
  };

  const handleRemoveFromCart = async (itemId: number) => {
    try {
      await api.delete(`/customer/cart/${itemId}`);

      const cartResponse = await api.get('/customer/cart');
      setCartItems(cartResponse.data);

      const statsResponse = await api.get('/customer/stats');
      setStats(statsResponse.data);
    } catch (err) {
      console.error('Erreur lors de la suppression du panier:', err);
      alert('Erreur lors de la suppression de l\'article. Veuillez réessayer.');
    }
  };

  const handleGoToCheckout = () => {
    navigate('/checkout');
  };

  const calculateCartTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0).toFixed(2);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-slate mb-8">Espace Client</h1>

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {successMessage}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-8 w-8 text-blue-600" />
            <div>
              <h3 className="text-slate font-medium mb-1">Commandes passées</h3>
              <p className="text-2xl font-bold text-terracotta">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="text-slate font-medium mb-1">Total dépensé</h3>
              <p className="text-2xl font-bold text-terracotta">{stats.totalSpent} FCFA</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-olive" />
            <div>
              <h3 className="text-slate font-medium mb-1">Articles dans le panier</h3>
              <p className="text-2xl font-bold text-terracotta">{stats.cartItemsCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        <button
          className={`py-2 px-4 whitespace-nowrap ${activeTab === 'products' ? 'border-b-2 border-olive text-olive' : 'text-gray-500'}`}
          onClick={() => setActiveTab('products')}
        >
          Produits
        </button>
        <button
          className={`py-2 px-4 whitespace-nowrap ${activeTab === 'cart' ? 'border-b-2 border-olive text-olive' : 'text-gray-500'}`}
          onClick={() => setActiveTab('cart')}
        >
          Panier ({cartItems.length})
        </button>
        <button
          className={`py-2 px-4 whitespace-nowrap ${activeTab === 'orders' ? 'border-b-2 border-olive text-olive' : 'text-gray-500'}`}
          onClick={() => setActiveTab('orders')}
        >
          Mes commandes
        </button>
        <button
          className={`py-2 px-4 whitespace-nowrap flex items-center gap-2 ${activeTab === 'negotiations' ? 'border-b-2 border-olive text-olive' : 'text-gray-500'}`}
          onClick={() => setActiveTab('negotiations')}
        >
          <TrendingUp className="h-4 w-4" />
          Négociations
        </button>

        <button
          className="py-2 px-4 whitespace-nowrap text-gray-500 hover:text-olive transition"
          onClick={() => navigate('/profile')}
        >
          Mon profil
        </button>

      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div>
          <h2 className="text-xl font-bold text-slate mb-4">Produits disponibles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.length === 0 ? (
              <p>Aucun produit disponible actuellement.</p>
            ) : (
              products.map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {product.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        className="w-full h-full object-cover"
                        src={product.image_url}
                        alt={product.name}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-lg">{product.price} FCFA</span>
                      <button
                        onClick={() => navigate('/products')}
                        disabled={product.stock <= 0}
                        className={`px-4 py-2 rounded-md ${
                          product.stock > 0
                            ? 'bg-olive text-white hover:bg-terracotta transition duration-300'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {product.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {product.stock} unité{product.stock !== 1 ? 's' : ''} en stock
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Cart Tab */}
      {activeTab === 'cart' && (
        <div>
          <h2 className="text-xl font-bold text-slate mb-4">Votre panier</h2>
          {cartItems.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="mb-4">Votre panier est vide</p>
              <button
                onClick={() => setActiveTab('products')}
                className="bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300"
              >
                Découvrir nos produits
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix unitaire</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cartItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.product.thumbnail_url && (
                              <div className="h-10 w-10 flex-shrink-0">
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={item.product.thumbnail_url}
                                  alt={item.product.name}
                                />
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{item.product.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.product.price} FCFA</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.quantity}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{(item.product.price * item.quantity).toFixed(2)} FCFA</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemoveFromCart(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-between items-center">
                <p className="text-lg font-semibold">Total: {calculateCartTotal()} FCFA</p>
                <button
                  onClick={handleGoToCheckout}
                  className="bg-olive text-white py-2 px-6 rounded-md hover:bg-terracotta transition duration-300"
                >
                  Passer commande
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          <h2 className="text-xl font-bold text-slate mb-4">Mes commandes</h2>
          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="mb-4">Vous n'avez pas encore passé de commande</p>
              <button
                onClick={() => setActiveTab('products')}
                className="bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300"
              >
                Découvrir nos produits
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">Commande #{order.id}</h3>
                      <p className="text-sm text-gray-600">
                        Date: {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      ORDER_STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {ORDER_STATUS_LABELS[order.status] || order.status}
                    </span>
                  </div>
                  <div className="px-6 py-4">
                    <p className="font-semibold mb-2">Articles:</p>
                    <ul className="space-y-2">
                      {order.items.map((item) => (
                        <li key={item.id} className="flex justify-between text-sm">
                          <span>{item.product_id} x{item.quantity}</span>
                          <span>{(item.price_per_unit * item.quantity).toFixed(2)} FCFA</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200">
                    <p className="text-lg font-semibold">Total: {order.total_price.toFixed(2)} FCFA</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Negotiations Tab */}
      {activeTab === 'negotiations' && (
        <CustomerNegotiationsTab />
      )}
    </div>
  );
};

export default CustomerDashboard;